// client/src/components/marketer/project/modals/ImageModal.js
import React, { useState, useEffect, useRef } from 'react';
import { MAX_GALLERY, MAX_TOTAL_BYTES, humanSize, sumBytes } from '../constants';
import AlertModal from './AlertModal';
import '../../../../styles/marketer/modals/ImageModal.css';

const checkImageSize = (file, targetW, targetH) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalWidth === targetW && img.naturalHeight === targetH);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });

export default function ImageModal({ open, onClose, initialCover, initialGallery, onSaved }) {
  const [localCover, setLocalCover] = useState(initialCover || null);
  const [localGallery, setLocalGallery] = useState(initialGallery || []);
  const [alertMsg, setAlertMsg] = useState('');

  const [coverSizeOk, setCoverSizeOk] = useState(null);
  const [gallerySizeOk, setGallerySizeOk] = useState([]);
  const [coverThumbUrl, setCoverThumbUrl] = useState(null);
  const [galleryThumbUrls, setGalleryThumbUrls] = useState([]);

  const coverFileRef = useRef(null);
  const gallerySnapshotRef = useRef([]);

  useEffect(() => {
    if (!localCover) { setCoverThumbUrl(null); return; }
    const url = URL.createObjectURL(localCover);
    setCoverThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [localCover]);

  useEffect(() => {
    if (localGallery.length === 0) { setGalleryThumbUrls([]); return; }
    const urls = localGallery.map(f => URL.createObjectURL(f));
    setGalleryThumbUrls(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [localGallery]);

  if (!open) return null;

  const checkTotal = (c, g) => {
    const total = (c?.size || 0) + sumBytes(g);
    if (total > MAX_TOTAL_BYTES) return `전체 용량 한도 100MB 초과 (${humanSize(total)})`;
    return '';
  };

  const onPickCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const msg = checkTotal(f, localGallery);
    if (msg) { setAlertMsg(msg); e.target.value = ''; return; }
    setLocalCover(f);
    setCoverSizeOk(null);
    coverFileRef.current = f;
    e.target.value = '';
    const ok = await checkImageSize(f, 600, 600);
    if (coverFileRef.current === f) setCoverSizeOk(ok);
  };

  const onPickGallery = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    if (localGallery.length + list.length > MAX_GALLERY) {
      setAlertMsg(`세부 사진은 최대 ${MAX_GALLERY}장`);
      e.target.value = '';
      return;
    }
    const next = [...localGallery, ...list];
    const msg = checkTotal(localCover, next);
    if (msg) { setAlertMsg(msg); e.target.value = ''; return; }
    setLocalGallery(next);
    gallerySnapshotRef.current = next;
    e.target.value = '';
    const newResults = await Promise.all(list.map(f => checkImageSize(f, 600, 600)));
    if (gallerySnapshotRef.current === next) {
      setGallerySizeOk(prev => [...prev, ...newResults]);
    }
  };

  const removeCover = () => {
    setLocalCover(null);
    setCoverSizeOk(null);
    coverFileRef.current = null;
  };

  const removeGalleryAt = (i) => {
    const next = localGallery.filter((_, idx) => idx !== i);
    setLocalGallery(next);
    setGallerySizeOk(prev => prev.filter((_, idx) => idx !== i));
    gallerySnapshotRef.current = next;
  };

  const saveLocal = () => {
    if (!localCover) { setAlertMsg('대표 사진은 필수입니다.'); return; }
    const msg = checkTotal(localCover, localGallery);
    if (msg) { setAlertMsg(msg); return; }
    onSaved(localCover, localGallery);
    onClose();
  };

  const totalSize = (localCover?.size || 0) + sumBytes(localGallery);
  const percentage = Math.round((totalSize / MAX_TOTAL_BYTES) * 100);

  return (
    <>
      <div className="image-modal mbm-overlay">
        <div className="image-modal__content mbm">
          <button onClick={onClose} aria-label="close" className="image-modal__close mbm-close">×</button>

          <div className="image-modal__header">
            <div className="image-modal__title mbm-title">광고할 제품/장소/서비스 사진을 첨부해주세요.</div>
            <div className="image-modal__subtitle">
              대표 사진 1장(필수), 세부 사진 최대 {MAX_GALLERY}장 (JPG, PNG, WEBP 지원)
            </div>
          </div>

          <div className="image-modal__body">
            {/* 대표 사진 섹션 */}
            <div className="image-modal__section">
              <div className="image-modal__section-header">
                <div className="image-modal__section-title-wrap">
                  <label className="image-modal__section-label">대표 사진 (필수) (600px X 600px)</label>
                  {coverSizeOk === null && (
                    <div className="image-modal__size-hint">권장 사이즈: 600 × 600px</div>
                  )}
                  {coverSizeOk === true && (
                    <div className="image-modal__size-ok">✅ 사이즈 정상 (600 × 600px)</div>
                  )}
                  {coverSizeOk === false && (
                    <div className="image-modal__size-warn">⚠️ 사이즈가 다릅니다. 이미지가 깨질 수 있습니다.</div>
                  )}
                </div>
                <label className="image-modal__upload-btn">
                  <span>📷 사진 선택</span>
                  <input type="file" accept="image/*" onChange={onPickCover} className="image-modal__file-input" />
                </label>
              </div>
              
              <div className="image-modal__file-list">
                {localCover ? (
                  <div className="image-modal__file-item">
                    <div className="image-modal__file-info">
                      {coverThumbUrl && (
                        <img
                          src={coverThumbUrl}
                          alt="대표 사진 미리보기"
                          className="image-modal__thumb"
                        />
                      )}
                      <div className="image-modal__file-details">
                        <span className="image-modal__file-name">{localCover.name}</span>
                        <span className="image-modal__file-size">{humanSize(localCover.size)}</span>
                      </div>
                    </div>
                    <button type="button" onClick={removeCover} className="image-modal__remove-btn">삭제</button>
                  </div>
                ) : (
                  <div className="image-modal__empty-state">
                    <span className="image-modal__empty-icon">📁</span>
                    <span className="image-modal__empty-text">선택된 사진이 없습니다</span>
                  </div>
                )}
              </div>
            </div>

            {/* 세부 사진 섹션 */}
            <div className="image-modal__section">
              <div className="image-modal__section-header">
                <div className="image-modal__section-title-wrap">
                  <label className="image-modal__section-label">
                    세부 사진 (선택) (600px X 600px)
                    {localGallery.length > 0 && (
                      <span className="image-modal__count-badge">{localGallery.length}/{MAX_GALLERY}</span>
                    )}
                  </label>
                </div>
                <label className="image-modal__upload-btn image-modal__upload-btn--secondary">
                  <span>📷 사진 추가</span>
                  <input type="file" accept="image/*" multiple onChange={onPickGallery} className="image-modal__file-input" />
                </label>
              </div>
              
              <div className="image-modal__file-list">
                {localGallery.length === 0 ? (
                  <div className="image-modal__empty-state">
                    <span className="image-modal__empty-icon">📁</span>
                    <span className="image-modal__empty-text">선택된 사진이 없습니다</span>
                  </div>
                ) : (
                  localGallery.map((f, i) => (
                    <div key={i} className="image-modal__file-item">
                      <div className="image-modal__file-info">
                        {galleryThumbUrls[i] && (
                          <img
                            src={galleryThumbUrls[i]}
                            alt={`세부 사진 ${i + 1} 미리보기`}
                            className="image-modal__thumb"
                          />
                        )}
                        <div className="image-modal__file-details">
                          <span className="image-modal__file-name">{f.name}</span>
                          <span className="image-modal__file-size">{humanSize(f.size)}</span>
                          {gallerySizeOk[i] === true && (
                            <span className="image-modal__size-ok">✅ 사이즈 정상</span>
                          )}
                          {gallerySizeOk[i] === false && (
                            <span className="image-modal__size-warn">⚠️ 사이즈가 다릅니다.</span>
                          )}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeGalleryAt(i)} className="image-modal__remove-btn">삭제</button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 용량 정보 */}
            <div className="image-modal__capacity">
              <div className="image-modal__capacity-header">
                <span className="image-modal__capacity-label">전체 용량</span>
                <span className="image-modal__capacity-text">
                  {humanSize(totalSize)} / {humanSize(MAX_TOTAL_BYTES)} ({percentage}%)
                </span>
              </div>
              <div className="image-modal__capacity-bar">
                <div 
                  className="image-modal__capacity-fill" 
                  style={{ 
                    width: `${Math.min(percentage, 100)}%`,
                    background: percentage > 90 ? '#e5484d' : percentage > 70 ? '#f59e0b' : '#2f6bff'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="image-modal__actions mbm-foot">
            <button type="button" onClick={onClose} className="image-modal__button image-modal__button--cancel mb-btn mb-btn-secondary">
              취소
            </button>
            <button type="button" onClick={saveLocal} className="image-modal__button image-modal__button--submit mb-btn mb-btn-primary">
              확인
            </button>
          </div>
        </div>
      </div>

      <AlertModal 
        open={!!alertMsg} 
        onClose={() => setAlertMsg('')} 
        message={alertMsg} 
      />
    </>
  );
}
